/**
 * Structured logging for procurement module
 * Provides consistent logging patterns with proper levels and context
 */

export type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';
export type LogContext = 'PROCUREMENT' | 'SUBCONS' | 'DATABASE' | 'REALTIME' | 'UI' | 'PERFORMANCE' | 'USER_ACTION' | 'COMPONENT_ERROR' | 'ERROR_BOUNDARY' | 'ROLLBACK' | 'BASELINE' | 'FEATURE_FLAGS' | 'PRODUCTION_INIT' | 'PRODUCTION_VALIDATION' | 'GLOBAL_PROCUREMENT' | 'PAYMENT_MERGER';

interface LogEntry {
  level: LogLevel;
  context: LogContext;
  message: string;
  metadata?: Record<string, any>;
  timestamp: string;
  sessionId?: string;
}

class ProcurementLogger {
  private sessionId: string;
  private isEnabled = true;

  constructor() {
    this.sessionId = this.generateSessionId();
  }

  private generateSessionId(): string {
    return `proc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private createLogEntry(
    level: LogLevel,
    context: LogContext,
    message: string,
    metadata?: Record<string, any>
  ): LogEntry {
    return {
      level,
      context,
      message,
      metadata,
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId
    };
  }

  private formatLog(entry: LogEntry): string {
    const { level, context, message, metadata, timestamp } = entry;
    const metaStr = metadata ? ` | ${JSON.stringify(metadata)}` : '';
    return `[${timestamp}] ${level} [${context}] ${message}${metaStr}`;
  }

  private log(entry: LogEntry): void {
    if (!this.isEnabled) return;

    // Only log DEBUG/INFO in development mode
    const isDev = import.meta.env.MODE === 'development';
    if (!isDev && entry.level !== 'ERROR' && entry.level !== 'WARN') {
      return;
    }

    const formatted = this.formatLog(entry);
    
    switch (entry.level) {
      case 'DEBUG':
        console.debug(formatted);
        break;
      case 'INFO':
        console.info(formatted);
        break;
      case 'WARN':
        console.warn(formatted);
        break;
      case 'ERROR':
        console.error(formatted);
        break;
    }
  }

  debug(context: LogContext, message: string, metadata?: Record<string, any>): void {
    this.log(this.createLogEntry('DEBUG', context, message, metadata));
  }

  info(context: LogContext, message: string, metadata?: Record<string, any>): void {
    this.log(this.createLogEntry('INFO', context, message, metadata));
  }

  warn(context: LogContext, message: string, metadata?: Record<string, any>): void {
    this.log(this.createLogEntry('WARN', context, message, metadata));
  }

  error(context: LogContext, message: string, metadata?: Record<string, any>): void {
    this.log(this.createLogEntry('ERROR', context, message, metadata));
  }

  // Performance timing utilities
  startTimer(operation: string): () => void {
    const start = performance.now();
    this.debug('PERFORMANCE', `Starting ${operation}`);
    
    return () => {
      const duration = performance.now() - start;
      this.info('PERFORMANCE', `Completed ${operation}`, { 
        duration: `${duration.toFixed(2)}ms`,
        operation 
      });
    };
  }

  // Database operation logging
  dbQuery(query: string, params?: Record<string, any>): void {
    this.debug('DATABASE', 'Executing query', { query, params });
  }

  dbResult(query: string, resultCount: number, duration?: number): void {
    this.info('DATABASE', 'Query completed', { 
      query, 
      resultCount, 
      duration: duration ? `${duration}ms` : undefined 
    });
  }

  dbError(query: string, error: any): void {
    this.error('DATABASE', 'Query failed', { 
      query, 
      error: error.message,
      code: error.code 
    });
  }

  // Realtime operation logging
  realtimeSubscribe(channel: string): void {
    this.info('REALTIME', 'Subscribing to channel', { channel });
  }

  realtimeEvent(channel: string, event: string, payload?: any): void {
    this.debug('REALTIME', 'Received event', { channel, event, payload });
  }

  realtimeError(channel: string, error: any): void {
    this.error('REALTIME', 'Realtime error', { channel, error: error.message });
  }

  // UI interaction logging
  userAction(action: string, details?: Record<string, any>): void {
    this.info('UI', `User action: ${action}`, details);
  }

  componentRender(component: string, props?: Record<string, any>): void {
    this.debug('UI', `Rendering component: ${component}`, props);
  }

  // Cache operations
  cacheHit(key: string): void {
    this.debug('PERFORMANCE', 'Cache hit', { key });
  }

  cacheMiss(key: string): void {
    this.debug('PERFORMANCE', 'Cache miss', { key });
  }

  cacheInvalidate(pattern: string): void {
    this.info('PERFORMANCE', 'Cache invalidated', { pattern });
  }

  // Toggle logging
  enable(): void {
    this.isEnabled = true;
  }

  disable(): void {
    this.isEnabled = false;
  }
}

// Export singleton instance
export const logger = new ProcurementLogger();

// Convenience loggers for specific contexts
export const procurementLogger = {
  debug: (message: string, metadata?: Record<string, any>) => 
    logger.debug('PROCUREMENT', message, metadata),
  info: (message: string, metadata?: Record<string, any>) => 
    logger.info('PROCUREMENT', message, metadata),
  warn: (message: string, metadata?: Record<string, any>) => 
    logger.warn('PROCUREMENT', message, metadata),
  error: (message: string, metadata?: Record<string, any>) => 
    logger.error('PROCUREMENT', message, metadata),
};

export const subconsLogger = {
  debug: (message: string, metadata?: Record<string, any>) => 
    logger.debug('SUBCONS', message, metadata),
  info: (message: string, metadata?: Record<string, any>) => 
    logger.info('SUBCONS', message, metadata),
  warn: (message: string, metadata?: Record<string, any>) => 
    logger.warn('SUBCONS', message, metadata),
  error: (message: string, metadata?: Record<string, any>) => 
    logger.error('SUBCONS', message, metadata),
};